import { useState } from "react";
import { recordFeedback, FeedbackRequest } from "../services/api";

interface FeedbackButtonsProps {
  messageId: string;
  sessionId: string;
  onFeedbackSubmitted?: () => void;
}

export function FeedbackButtons({ messageId, sessionId, onFeedbackSubmitted }: FeedbackButtonsProps) {
  const [resolution, setResolution] = useState<"yes" | "no" | "made_worse" | "fixed" | null>(null);
  const [rating, setRating] = useState<number | null>(null);
  const [notes, setNotes] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  const handleSubmitFeedback = async () => {
    if (!resolution) {
      setError("Please select a resolution status");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const feedback: FeedbackRequest = {
        session_id: sessionId,
        message_response_id: messageId,
        resolution_status: resolution,
        helpfulness_rating: rating || undefined,
        user_notes: notes || undefined,
      };

      await recordFeedback(feedback);
      setSubmitted(true);
      setShowForm(false);
      onFeedbackSubmitted?.();

      // Reset form after 3 seconds
      setTimeout(() => {
        setResolution(null);
        setRating(null);
        setNotes("");
        setSubmitted(false);
      }, 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit feedback");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="mt-3 border-t border-gray-100 pt-3">
        <div className="flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-700">
          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
          Thanks for your feedback!
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 border-t border-gray-100 pt-3">
      {!showForm ? (
        <button
          onClick={() => setShowForm(true)}
          className="text-xs font-medium text-gray-500 hover:text-gray-700 transition"
        >
          Was this helpful? Give feedback
        </button>
      ) : (
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-2 block">Did this help?</label>
            <div className="flex gap-2">
              {(["yes", "no", "made_worse", "fixed"] as const).map((option) => (
                <button
                  key={option}
                  onClick={() => setResolution(option)}
                  className={`px-3 py-1.5 rounded text-xs font-medium transition ${
                    resolution === option
                      ? "bg-brand-accent text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {option === "made_worse" ? "Made Worse" : option.charAt(0).toUpperCase() + option.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600 mb-2 block">How helpful was this?</label>
            <div className="flex gap-0.5">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  className={`rounded p-0.5 text-lg transition hover:scale-110 ${
                    rating !== null && star <= rating ? "text-amber-400" : "text-gray-300 hover:text-amber-300"
                  }`}
                  aria-label={`Rate ${star} out of 5`}
                >
                  ★
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Add comments (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Your feedback helps us improve..."
              className="w-full px-2 py-1.5 rounded border border-gray-200 text-xs placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent"
              rows={2}
            />
          </div>

          {error && (
            <div className="rounded bg-red-50 px-2 py-1.5 text-xs text-red-700 border border-red-200">
              {error}
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={handleSubmitFeedback}
              disabled={loading}
              className="px-3 py-1.5 rounded text-xs font-medium bg-brand-accent text-white hover:opacity-90 disabled:opacity-50 transition"
            >
              {loading ? "Submitting..." : "Submit Feedback"}
            </button>
            <button
              onClick={() => setShowForm(false)}
              className="px-3 py-1.5 rounded text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      <span className="sr-only">Message {messageId}</span>
    </div>
  );
}
